import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FolderPlus, ArrowLeft, Calculator } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewCase() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    client_name: "",
    address: "",
    description: "",
    project_type: "residential",
    category: "renovation",
    region: "",
    estimated_value: "",
    deadline: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.client_name || !formData.address) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        estimated_value: formData.estimated_value ? parseFloat(formData.estimated_value) : null,
      };
      const response = await axios.post(`${API}/cases`, payload);
      toast.success("Estimate case created!");
      navigate(`/case/${response.data.id}`);
    } catch (error) {
      console.error("Error creating case:", error);
      toast.error("Failed to create case");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="new-case-page">
      <div className="mb-8">
        <Button variant="ghost" size="sm" className="mb-4 gap-2" onClick={() => navigate("/")}>
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </Button>
        <h1 className="text-2xl lg:text-3xl font-heading font-bold text-foreground">New Estimate Case</h1>
        <p className="text-muted-foreground mt-1">Enter project details to start your estimate</p>
      </div>

      <div className="swiss-card">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="name" className="font-medium">
              Project Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              name="name"
              placeholder="e.g., Kitchen Renovation - Smith Residence"
              value={formData.name}
              onChange={handleChange}
              data-testid="project-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="client_name" className="font-medium">
              Client Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="client_name"
              name="client_name"
              placeholder="e.g., John Smith"
              value={formData.client_name}
              onChange={handleChange}
              data-testid="client-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address" className="font-medium">
              Project Address <span className="text-destructive">*</span>
            </Label>
            <Input
              id="address"
              name="address"
              placeholder="e.g., 123 Main Street, Miami, FL 33101"
              value={formData.address}
              onChange={handleChange}
              data-testid="address-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="project_type" className="font-medium">Project Type</Label>
              <Select
                value={formData.project_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, project_type: value }))}
              >
                <SelectTrigger data-testid="project-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="residential">Residential</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="town_house">Town House</SelectItem>
                  <SelectItem value="retail">Retail</SelectItem>
                  <SelectItem value="office">Office</SelectItem>
                  <SelectItem value="industrial">Industrial</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category" className="font-medium">Category</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
                <SelectTrigger data-testid="category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="renovation">Renovation</SelectItem>
                  <SelectItem value="ground_up">Ground-up</SelectItem>
                  <SelectItem value="tenant_improvement">Tenant Improvement</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="region" className="font-medium">Region</Label>
              <Select value={formData.region} onValueChange={(value) => setFormData(prev => ({ ...prev, region: value }))}>
                <SelectTrigger data-testid="region-select"><SelectValue placeholder="Select region" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Miami">Miami</SelectItem>
                  <SelectItem value="Orlando">Orlando</SelectItem>
                  <SelectItem value="Tampa">Tampa</SelectItem>
                  <SelectItem value="Jacksonville">Jacksonville</SelectItem>
                  <SelectItem value="Fort Lauderdale">Fort Lauderdale</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="estimated_value" className="font-medium">Estimated Value ($)</Label>
              <Input id="estimated_value" name="estimated_value" type="number" placeholder="e.g. 500000"
                value={formData.estimated_value} onChange={handleChange} data-testid="value-input" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="deadline" className="font-medium">Deadline</Label>
              <Input
                id="deadline"
                name="deadline"
                type="date"
                value={formData.deadline}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="font-medium">Project Description</Label>
            <Textarea
              id="description"
              name="description"
              placeholder="Describe the scope of work, main objectives, and any special requirements..."
              value={formData.description}
              onChange={handleChange}
              rows={4}
              data-testid="description-textarea"
            />
          </div>

          <div className="flex items-center justify-end gap-4 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={() => navigate("/")}>Cancel</Button>
            <Button type="submit" disabled={loading} className="gap-2" data-testid="create-case-btn">
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Creating...</>
              ) : (
                <><FolderPlus className="w-4 h-4" /> Create Case</>
              )}
            </Button>
          </div>
        </form>
      </div>

      <div className="swiss-card mt-6 bg-primary/5 border-primary/20">
        <div className="flex gap-4">
          <Calculator className="w-10 h-10 text-primary flex-shrink-0" />
          <div>
            <h3 className="font-heading font-semibold mb-1">9-Step Workflow</h3>
            <p className="text-sm text-muted-foreground">
              After creating, you'll go through: Scope Analysis → Hidden Work → 
              Compliance → Cost Codes → Pricing → Bidding → Leveling → Final Estimate
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
